/**
 * EchoPlayer.js — Audio player with word-level transcript sync, screenshot preview,
 * +/-10s seek, per-segment editing (admin only), and transcript save API.
 */
import { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";
import { C, ATTACH_BASE } from "./utils";
import { ThemeCtx } from "./AppShell";

const API = "https://cip.medsphere.net/api";
const attachUrl = p => ATTACH_BASE + p;
const isAudioFile = p => /\.(webm|mp3|mp4|mpeg|mpga|m4a|wav|ogg|aac|caf)$/i.test(p||"");
const isImageFile = p => /\.(png|jpg|jpeg|gif|webp)$/i.test(p||"");

function toSeconds(val) {
  if(!val) return 0;
  if(typeof val==="string"&&val.includes(":")) {
    const p=val.split(":").map(Number);
    return p.length===3?p[0]*3600+p[1]*60+p[2]:p[0]*60+(p[1]||0);
  }
  return Number(val)||0;
}

export function hasEchoContent(chatbot) {
  if(!Array.isArray(chatbot)||!chatbot.length) return false;
  return chatbot.some(f =>
    isAudioFile(f.FILEPATH) ||
    (f.TRANSCRIPT&&f.TRANSCRIPT.trim().length>0) ||
    (toSeconds(f.SCREENTIME)>0&&isImageFile(f.FILEPATH))
  );
}

function fmtTime(s) {
  const n=toSeconds(s);
  const m=Math.floor(n/60),sec=Math.floor(n%60);
  return `${m}:${String(sec).padStart(2,"0")}`;
}

// ─── Parse transcript HTML into segments ─────────────────────────────────────
// ─── Parse transcript — handles both PHP HTML format and Python plain text ────
function parseSegments(html) {
  if(!html) return [];

  // ── detect format ──────────────────────────────────────────────────────────
  const hasHtmlSpeaker = /<span[^>]*font-weight\s*:\s*bold[^>]*>/i.test(html);

  if(hasHtmlSpeaker) {
    // ── PHP / HTML format: <br><span ...>SPEAKER X:</span> body text ──────────
    const lines = html.split(/<br\s*\/?>/gi).map(l=>l.trim()).filter(Boolean);
    return lines.map(line=>{
      const tm = line.match(/\[?(\d{1,2}):(\d{2})(?::(\d{2}))?\]?/);
      const secs = tm?(tm[3]?Number(tm[1])*3600+Number(tm[2])*60+Number(tm[3]):Number(tm[1])*60+Number(tm[2])):null;
      const cleanHtml = line.replace(/\[?\d{1,2}:\d{2}(?::\d{2})?\]?\s*/,"").trim();
      const div=document.createElement("div"); div.innerHTML=cleanHtml;
      const plainText=(div.textContent||div.innerText||"").trim();
      const isSpeaker=/<span[^>]*font-weight/i.test(cleanHtml);
      return {html:cleanHtml, plainText, secs, isSpeaker};
    }).filter(s=>s.plainText.length>0);
  }

  // ── Python / plain text format: SPEAKER A: words SPEAKER B: words ──────────
  // may be all on one line or across multiple lines
  const fullText = html
    .replace(/<br\s*\/?>/gi," ")
    .replace(/<[^>]+>/g,"")
    .replace(/&amp;/g,"&").replace(/&lt;/g,"<").replace(/&gt;/g,">").replace(/&#039;/g,"'")
    .trim();

  // split on SPEAKER X: pattern
  const parts = fullText.split(/(?=SPEAKER\s+[A-Z]:\s)/i);
  return parts.map(part=>{
    const part2 = part.trim(); if(!part2) return null;
    const spMatch = part2.match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/is);
    if(spMatch) {
      const label = spMatch[1].trim();
      const body  = spMatch[2].trim();
      // color speaker label to match PHP style
      const color = label.includes("A")?"#E67E22":label.includes("B")?"#2980B9":label.includes("C")?"#27AE60":"#8E44AD";
      const spanHtml = `<span style="color:${color};font-weight:bold;">${label}</span>`;
      return {
        html: `${spanHtml} ${body}`,
        plainText: `${label} ${body}`,
        secs: null, isSpeaker: true
      };
    }
    return {html:part2, plainText:part2, secs:null, isSpeaker:false};
  }).filter(Boolean).filter(s=>s.plainText.trim().length>0);
}

// ─── Segment-level timing based on word count at ~140wpm ─────────────────────
function buildSegmentTimings(segments, duration) {
  if(!duration||!segments.length||!isFinite(duration)||duration<=0) return [];

  const wordCount = (seg) => {
    const body = seg.plainText.replace(/^SPEAKER\s+[A-Z]:\s*/i,"").trim();
    return body.split(/\s+/).filter(Boolean).length || 1;
  };

  const segsWithTime = segments.filter(s=>s.secs!==null);

  if(segsWithTime.length>0) {
    // use embedded timestamps directly
    return segments.map((seg,i)=>{
      const start = seg.secs!==null ? seg.secs
        : (segments.slice(0,i).reverse().find(s=>s.secs!==null)?.secs||0);
      const next  = segments.slice(i+1).find(s=>s.secs!==null);
      const end   = next ? next.secs : duration;
      return {segIdx:i, start, end};
    });
  }

  // no timestamps — use word count proportional timing
  // average speaking rate: 140 wpm = 0.43 sec/word
  const totalWords = segments.reduce((acc,s)=>acc+wordCount(s), 0);
  // fit to actual duration so we don't go over
  const secPerWord = duration / totalWords;
  let t = 0;
  return segments.map((seg,i)=>{
    const words = wordCount(seg);
    const start = t;
    t += words * secPerWord;
    return {segIdx:i, start, end:t};
  });
}

// ─── Segment component ────────────────────────────────────────────────────────
// ─── Segment — segment-level highlight only ───────────────────────────────────
function Segment({ seg, idx, isActive, isAdmin, onSeek, primary, onLocalSave }) {
  const [editing,    setEditing]    = useState(false);
  const [editText,   setEditText]   = useState("");
  const [editedHtml, setEditedHtml] = useState(seg.html);
  const taRef = useRef(null);

  const getSpeaker = (html) => {
    const hm = html.match(/^(<span[^>]*>(?:SPEAKER\s+[A-Z]:|[^<]{1,30})<\/span>)\s*/i);
    if(hm) return hm[1];
    const pm = html.match(/^(SPEAKER\s+[A-Z]:\s*)/i);
    if(pm) {
      const label=pm[1].trim();
      const color=label.includes("A")?"#E67E22":label.includes("B")?"#2980B9":label.includes("C")?"#27AE60":"#8E44AD";
      return `<span style="color:${color};font-weight:bold;">${label}</span>`;
    }
    return "";
  };
  const getBody = (html) => {
    let s=html.replace(/^<span[^>]*>(?:SPEAKER\s+[A-Z]:|[^<]{1,30})<\/span>\s*/i,"");
    s=s.replace(/^SPEAKER\s+[A-Z]:\s*/i,"");
    const div=document.createElement("div"); div.innerHTML=s;
    return (div.textContent||div.innerText||"").trim();
  };

  const startEdit = (e) => {
    e.stopPropagation();
    setEditText(getBody(editedHtml));
    setEditing(true);
    setTimeout(()=>{taRef.current?.focus();taRef.current?.select();},60);
  };
  const confirmEdit = () => {
    if(!editText.trim()) return;
    const sp=getSpeaker(seg.html);
    const newHtml=sp?`${sp} ${editText.trim()}`:editText.trim();
    setEditedHtml(newHtml); setEditing(false); onLocalSave(idx,newHtml);
  };
  const resetEdit = (e) => { e.stopPropagation(); setEditedHtml(seg.html); onLocalSave(idx,seg.html); };
  const cancel = () => setEditing(false);
  const handleKey = (e) => {
    if(e.ctrlKey&&e.key==="Enter"){e.preventDefault();confirmEdit();}
    if(e.key==="Escape") cancel();
  };
  const isEdited = editedHtml!==seg.html;

  return (
    <div style={{marginBottom:5,padding:"6px 8px",borderRadius:7,
      transition:"background .2s,border-left .2s",
      background:isActive?`${primary}14`:"transparent",
      borderLeft:`3px solid ${isActive?primary:isEdited?"#F59E0B":"transparent"}`,
      cursor:seg.secs!==null&&!editing?"pointer":"default"}}
      onClick={()=>seg.secs!==null&&!editing&&onSeek(seg.secs)}>
      {editing?(
        <div onClick={e=>e.stopPropagation()}>
          {getSpeaker(editedHtml)&&(
            <div style={{fontSize:11,marginBottom:4}}
              dangerouslySetInnerHTML={{__html:getSpeaker(editedHtml)}}/>
          )}
          <textarea ref={taRef} value={editText} onChange={e=>setEditText(e.target.value)}
            onKeyDown={handleKey} rows={3} placeholder="Edit spoken content…"
            style={{width:"100%",boxSizing:"border-box",padding:"6px 8px",borderRadius:6,
              border:`1.5px solid ${primary}`,outline:"none",fontSize:12,
              fontFamily:"inherit",lineHeight:1.6,resize:"vertical"}}/>
          <div style={{display:"flex",gap:6,marginTop:4,alignItems:"center"}}>
            <button onClick={confirmEdit}
              style={{padding:"3px 12px",borderRadius:5,border:"none",
                background:primary,color:"#fff",fontSize:11,fontWeight:600,cursor:"pointer"}}>
              ✓ Done
            </button>
            <button onClick={cancel}
              style={{padding:"3px 10px",borderRadius:5,border:"1px solid #E5E7EB",
                background:"#F7F9FC",color:"#6B7280",fontSize:11,cursor:"pointer"}}>
              Cancel
            </button>
            <span style={{fontSize:10,color:"#9CA3AF",marginLeft:"auto"}}>Ctrl+Enter</span>
          </div>
        </div>
      ):(
        <div style={{display:"flex",alignItems:"flex-start",gap:6}}>
          {seg.secs!==null&&(
            <span style={{fontSize:9,fontFamily:"monospace",flexShrink:0,
              background:`${primary}18`,color:primary,borderRadius:4,
              padding:"1px 5px",marginTop:3,fontWeight:600,minWidth:32,textAlign:"center"}}>
              {fmtTime(seg.secs)}
            </span>
          )}
          <span style={{fontSize:12,lineHeight:1.8,flex:1,
            color:isActive?"#111":"#374151",fontWeight:isActive?500:400,
            transition:"color .2s,font-weight .2s"}}
            dangerouslySetInnerHTML={{__html:editedHtml}}/>
          <div style={{display:"flex",gap:3,flexShrink:0,marginTop:2,alignItems:"center"}}>
            {isEdited&&(
              <button onClick={resetEdit} title="Undo edit"
                style={{background:"none",border:"1px solid #FECACA",cursor:"pointer",
                  padding:"2px 6px",borderRadius:5,color:"#EF4444",fontSize:10,fontWeight:700}}
                onMouseEnter={e=>e.currentTarget.style.background="#FEF2F2"}
                onMouseLeave={e=>e.currentTarget.style.background="none"}>↩</button>
            )}
            {isAdmin&&(
              <button onClick={startEdit} title="Edit"
                style={{background:"none",border:"1px solid #E5E7EB",cursor:"pointer",
                  padding:"2px 5px",borderRadius:5,color:"#9CA3AF",transition:"all .12s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=primary;e.currentTarget.style.color=primary;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#E5E7EB";e.currentTarget.style.color="#9CA3AF";}}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ScreenshotPanel({ screenshots, currentTime, onSeek, primary, imageView }) {
  if(!screenshots.length) return null;
  const activeIdx = screenshots.reduce((bi,s,i)=>{
    const t=toSeconds(s.SCREENTIME);
    return t<=currentTime&&t>toSeconds(screenshots[bi]?.SCREENTIME||0)?i:bi;
  },0);
  const active=screenshots[activeIdx];
  const canPrev=activeIdx>0;
  const canNext=activeIdx<screenshots.length-1;
  return(
    <div style={{display:"flex",flexDirection:"column",gap:6,height:"100%",overflow:"hidden"}}>
      {/* large active image with prev/next */}
      <div style={{borderRadius:8,overflow:"hidden",border:`2px solid ${primary}`,
        background:"#000",flexShrink:0,position:"relative"}}>
        <img src={attachUrl(active.FILEPATH)} alt=""
          style={{width:"100%",maxHeight:imageView?260:130,objectFit:"contain",display:"block"}}/>
        {/* prev/next overlay buttons */}
        {canPrev&&(
          <button onClick={()=>onSeek(toSeconds(screenshots[activeIdx-1].SCREENTIME))}
            style={{position:"absolute",left:4,top:"50%",transform:"translateY(-50%)",
              background:"rgba(0,0,0,0.5)",border:"none",borderRadius:"50%",
              width:28,height:28,cursor:"pointer",color:"#fff",fontSize:16,
              display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
        )}
        {canNext&&(
          <button onClick={()=>onSeek(toSeconds(screenshots[activeIdx+1].SCREENTIME))}
            style={{position:"absolute",right:4,top:"50%",transform:"translateY(-50%)",
              background:"rgba(0,0,0,0.5)",border:"none",borderRadius:"50%",
              width:28,height:28,cursor:"pointer",color:"#fff",fontSize:16,
              display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
        )}
        <div style={{padding:"2px 8px",background:"rgba(0,0,0,0.7)",
          color:"#fff",fontSize:10,fontFamily:"monospace",textAlign:"center",
          display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <span>{fmtTime(toSeconds(active.SCREENTIME))}</span>
          <span style={{opacity:0.6}}>{activeIdx+1} / {screenshots.length}</span>
        </div>
      </div>
      {/* thumbnails */}
      <div style={{flex:1,overflowY:"auto",display:"flex",
        flexDirection:imageView?"row":"column",flexWrap:imageView?"wrap":"nowrap",gap:4}}>
        {screenshots.map((s,i)=>{
          const isCur=i===activeIdx;
          return(
            <div key={s.ATTACHMENTID} onClick={()=>onSeek(toSeconds(s.SCREENTIME))}
              style={{cursor:"pointer",borderRadius:5,overflow:"hidden",flexShrink:0,
                width:imageView?"calc(50% - 2px)":undefined,
                border:`2px solid ${isCur?primary:"#E5E7EB"}`,
                opacity:isCur?1:0.6,transition:"all .15s"}}
              onMouseEnter={e=>e.currentTarget.style.opacity="1"}
              onMouseLeave={e=>e.currentTarget.style.opacity=isCur?"1":"0.6"}>
              <img src={attachUrl(s.FILEPATH)} alt=""
                style={{width:"100%",height:imageView?60:52,objectFit:"cover",display:"block"}}/>
              <div style={{padding:"1px 6px",fontSize:9,color:C.hint,
                fontFamily:"monospace",background:"#F8FAFC",borderTop:"1px solid #E5E7EB"}}>
                {fmtTime(toSeconds(s.SCREENTIME))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main EchoPlayer ──────────────────────────────────────────────────────────
export default function EchoPlayer({ chatbot, user, activeSub, documentId, docTitle, onClose }) {
  const { theme }  = useContext(ThemeCtx)||{};
  const primary    = theme?.primary||C.primary;
  const isAdmin    = activeSub?.ROLE===1;
  const audioRef   = useRef(null);
  const animRef    = useRef(null);
  const transRef   = useRef(null);

  const audio       = chatbot.find(f=>isAudioFile(f.FILEPATH));
  const screenshots = chatbot
    .filter(f=>isImageFile(f.FILEPATH)&&toSeconds(f.SCREENTIME)>0)
    .sort((a,b)=>toSeconds(a.SCREENTIME)-toSeconds(b.SCREENTIME));
  const pdfs = chatbot.filter(f=>/\.pdf$/i.test(f.FILEPATH));

  const [playing,     setPlaying]     = useState(false);
  const [current,     setCurrent]     = useState(0);
  const [duration,    setDuration]    = useState(0);
  const [copied,      setCopied]      = useState(false);
  const [segments,    setSegments]    = useState(()=>parseSegments(audio?.TRANSCRIPT||""));
  const [pendingEdits,setPendingEdits]= useState(0);
  const [serverSaving,setServerSaving]= useState(false);
  const [serverSaved, setServerSaved] = useState(false);
  const [imageView,   setImageView]   = useState(false); // flip layout: image left, transcript right

  // rebuild full transcript HTML from segments and POST to server
  const saveToServer = async () => {
    if(!user||!activeSub||!documentId||!audio) return;
    setServerSaving(true);
    try {
      const fullTranscript = segments.map(s=>{
        const div=document.createElement("div"); div.innerHTML=s.html;
        return div.textContent||div.innerText||"";
      }).join("\n");
      const fd=new FormData();
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub.SUBPROJECTID);
      fd.append("documentid",   documentId);
      fd.append("attachmentid", audio.ATTACHMENTID);
      fd.append("stateid",      2);
      fd.append("transcript",   fullTranscript);
      const r=await fetch(`${API}/edit_transcript`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(res.STATUS==="1"||res.STATUS===1){
        setPendingEdits(0);
        setServerSaved(true);
        setTimeout(()=>setServerSaved(false),3000);
      }
    } catch{}
    setServerSaving(false);
  };

  const segmentTimings = useMemo(()=>{
    if(!duration||!segments.length) return [];
    return buildSegmentTimings(segments, duration);
  },[segments,duration]);

  // use ref for current time so rAF always has fresh value
  const currentRef     = useRef(0);

  const [activeInfo, setActiveInfo] = useState({segIdx:-1});
  const activeIdx = activeInfo.segIdx;

  const seek = useCallback((secs)=>{
    const a=audioRef.current; if(!a) return;
    a.currentTime=Math.max(0,Math.min(secs,a.duration||0));
    a.play(); setPlaying(true);
  },[]);

  const skip = (delta) => {
    const a=audioRef.current; if(!a) return;
    a.currentTime=Math.max(0,Math.min(a.currentTime+delta,a.duration||0));
  };

  const togglePlay = ()=>{
    const a=audioRef.current; if(!a) return;
    if(a.paused){a.play();setPlaying(true);}
    else{a.pause();setPlaying(false);}
  };

  const seekBar = (e)=>{
    const a=audioRef.current; if(!a||!duration) return;
    const r=e.currentTarget.getBoundingClientRect();
    a.currentTime=((e.clientX-r.left)/r.width)*duration;
  };

  const segTimingsRef  = useRef([]);

  useEffect(()=>{ segTimingsRef.current = segmentTimings; },[segmentTimings]);

  // rAF loop — registers once, reads wordTimings via ref
  useEffect(()=>{
    const a=audioRef.current; if(!a) return;
    const tick=()=>{
      const t=a.currentTime||0;
      currentRef.current=t;
      setCurrent(t);
      const wt=segTimingsRef.current;
      if(wt.length){
        let segIdx=-1;
        for(let i=wt.length-1;i>=0;i--){
          if(wt[i].start<=t){ segIdx=wt[i].segIdx; break; }
        }
        setActiveInfo(prev=>{
          if(segIdx===prev.segIdx) return prev;
          return {segIdx};
        });
      }
      animRef.current=requestAnimationFrame(tick);
    };
    const start=()=>{animRef.current=requestAnimationFrame(tick);};
    const stop=()=>{cancelAnimationFrame(animRef.current);};
    a.addEventListener("play",start); a.addEventListener("pause",stop);
    a.addEventListener("ended",()=>{stop();setPlaying(false);});
    return()=>{a.removeEventListener("play",start);a.removeEventListener("pause",stop);stop();};
  },[]);  // ← runs once only

  // auto-scroll active segment
  useEffect(()=>{
    if(!transRef.current||activeIdx<0) return;
    const el=transRef.current.querySelector(`[data-seg="${activeIdx}"]`);
    if(el) el.scrollIntoView({behavior:"smooth",block:"nearest"});
  },[activeIdx]);

  const copyText = ()=>{
    const div=document.createElement("div"); div.innerHTML=audio?.TRANSCRIPT||"";
    navigator.clipboard.writeText(div.textContent||"").then(()=>{
      setCopied(true); setTimeout(()=>setCopied(false),1800);
    });
  };

  const downloadWord = async ()=>{
    const heading  = (docTitle||"Transcript").trim();
    const now      = new Date();
    const pad      = n=>String(n).padStart(2,"0");
    const timeStr  = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    const fileName = `Transcript_${timeStr}.docx`;

    const escXml = s => (s||"")
      .replace(/&/g,"&amp;")
      .replace(/</g,"&lt;")
      .replace(/>/g,"&gt;")
      .replace(/"/g,"&quot;")
      .replace(/'/g,"&apos;");

    // Build paragraphs from segments
    const paras = [];

    // Title paragraph
    paras.push(`
      <w:p>
        <w:pPr><w:pStyle w:val="Heading1"/><w:spacing w:after="200"/></w:pPr>
        <w:r><w:t>${escXml(heading)}</w:t></w:r>
      </w:p>`);

    if(segments.length > 0){
      segments.forEach(seg=>{
        const text = seg.plainText||"";
        const m = seg.isSpeaker && text.match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/is);
        if(m){
          // speaker label bold + body in same paragraph
          paras.push(`
            <w:p>
              <w:pPr><w:spacing w:before="120" w:after="60"/></w:pPr>
              <w:r><w:rPr><w:b/><w:color w:val="E67E22"/></w:rPr><w:t xml:space="preserve">${escXml(m[1].trim())} </w:t></w:r>
              <w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t>${escXml(m[2].trim())}</w:t></w:r>
            </w:p>`);
        } else {
          paras.push(`
            <w:p>
              <w:pPr><w:spacing w:before="0" w:after="60"/></w:pPr>
              <w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t xml:space="preserve">${escXml(text)}</w:t></w:r>
            </w:p>`);
        }
      });
    } else {
      const div = document.createElement("div");
      div.innerHTML = audio?.TRANSCRIPT||"";
      (div.textContent||"").split("\n").filter(l=>l.trim()).forEach(line=>{
        paras.push(`
          <w:p>
            <w:r><w:t xml:space="preserve">${escXml(line)}</w:t></w:r>
          </w:p>`);
      });
    }

    const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas"
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    ${paras.join("\n")}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/>
    </w:sectPr>
  </w:body>
</w:document>`;

    const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:rPr><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr>
  </w:style>
  <w:style w:type="paragraph" w:styleId="Heading1">
    <w:name w:val="heading 1"/>
    <w:basedOn w:val="Normal"/>
    <w:rPr><w:b/><w:sz w:val="36"/><w:szCs w:val="36"/><w:color w:val="1F3864"/></w:rPr>
  </w:style>
</w:styles>`;

    const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

    const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
</Types>`;

    const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

    // Build ZIP using JSZip if available, else fall back to RTF
    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      zip.file("[Content_Types].xml",  contentTypesXml);
      zip.file("_rels/.rels",          rootRelsXml);
      zip.file("word/document.xml",    documentXml);
      zip.file("word/styles.xml",      stylesXml);
      zip.file("word/_rels/document.xml.rels", relsXml);
      const blob = await zip.generateAsync({type:"blob",
        mimeType:"application/vnd.openxmlformats-officedocument.wordprocessingml.document"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; a.click();
      setTimeout(()=>URL.revokeObjectURL(url), 1000);
    } catch {
      // JSZip not available — fall back to RTF
      const escRtf = s => (s||"").replace(/\\/g,"\\\\").replace(/\{/g,"\\{").replace(/\}/g,"\\}").replace(/[^\x00-\x7F]/g,c=>`\\u${c.charCodeAt(0)}\\'3f`);
      let body = "";
      segments.forEach(seg=>{
        const m = seg.isSpeaker&&(seg.plainText||"").match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/is);
        if(m) body+=`{\\b\\cf1 ${escRtf(m[1].trim())}}\\b0\\cf0  ${escRtf(m[2].trim())}\\par\\par\n`;
        else  body+=escRtf(seg.plainText||"")+"\\par\n";
      });
      const rtf=`{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fswiss Arial;}}{\\colortbl;\\red230\\green126\\blue34;}\\f0\\fs24{\\pard\\b\\fs30 ${escRtf(heading)}\\b0\\par}\\par\n${body}}`;
      const blob=new Blob([rtf],{type:"application/rtf"});
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url; a.download=fileName.replace(".docx",".rtf"); a.click();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    }
  };

  if(!audio) return(
    <div style={{padding:24,textAlign:"center",color:C.hint,fontSize:13}}>
      No audio file found.
    </div>
  );

  const pct=duration?(current/duration)*100:0;
  const nm=audio.FILEPATH.replace(/_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}\./,".");
  const hasTranscript=segments.length>0&&(audio.TRANSCRIPT?.trim().length>0);

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",
      background:"#F8FAFC",overflow:"hidden"}}>

      {/* header */}
      <div style={{flexShrink:0,background:"#fff",borderBottom:`1px solid ${C.border}`,
        padding:"10px 16px",display:"flex",alignItems:"center",gap:10,minHeight:46}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            🎙 {nm}
          </div>
          <div style={{fontSize:10,color:C.hint,marginTop:1}}>
            {screenshots.length>0&&`${screenshots.length} slide${screenshots.length!==1?"s":""} · `}
            {hasTranscript?"Recording with transcript":"Recording"}
          </div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
          {hasTranscript&&(
            <>
              <button onClick={copyText}
                style={{padding:"5px 11px",borderRadius:6,border:"1px solid #E5E7EB",
                  background:copied?"#E1F5EE":"#F7F9FC",color:copied?"#16A34A":C.muted,
                  fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4,
                  whiteSpace:"nowrap"}}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="9" y="9" width="13" height="13" rx="2"/>
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
                {copied?"Copied!":"Copy"}
              </button>
              <button onClick={downloadWord}
                style={{padding:"5px 11px",borderRadius:6,border:"1px solid #E5E7EB",
                  background:"#F7F9FC",color:C.muted,
                  fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4,
                  whiteSpace:"nowrap"}}
                title="Download as Word document">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <path d="M12 18v-6M9 15l3 3 3-3"/>
                </svg>
                Word
              </button>
              {pdfs.map((pdf,i)=>(
                <a key={i} href={attachUrl(pdf.FILEPATH)} target="_blank" rel="noopener noreferrer"
                  download
                  style={{padding:"5px 11px",borderRadius:6,border:"1px solid #E5E7EB",
                    background:"#F7F9FC",color:"#EF4444",textDecoration:"none",
                    fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4,
                    whiteSpace:"nowrap"}}
                  title="Download PDF">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <polyline points="9 10 12 13 15 10"/>
                    <line x1="12" y1="3" x2="12" y2="13"/>
                  </svg>
                  {pdfs.length>1?`PDF ${i+1}`:"PDF"}
                </a>
              ))}
            </>
          )}
          {onClose&&(
            <button onClick={onClose}
              style={{width:28,height:28,borderRadius:6,border:"1px solid #E5E7EB",
                background:"#F7F9FC",cursor:"pointer",fontSize:18,color:"#6B7280",
                display:"flex",alignItems:"center",justifyContent:"center",
                lineHeight:1,flexShrink:0}}
              title="Close">×</button>
          )}
        </div>
      </div>

      {/* player */}
      <div style={{flexShrink:0,padding:"10px 14px",background:"#fff",
        borderBottom:`1px solid ${C.border}`}}>
        <audio ref={audioRef} src={attachUrl(audio.FILEPATH)} preload="metadata"
          type={/\.mp4$/i.test(audio.FILEPATH||"")?"audio/mp4":/\.m4a$/i.test(audio.FILEPATH||"")?"audio/mp4":/\.wav$/i.test(audio.FILEPATH||"")?"audio/wav":/\.webm$/i.test(audio.FILEPATH||"")?"audio/webm":"audio/mpeg"}
          onLoadedMetadata={()=>{
            const a = audioRef.current; if(!a) return;
            const d = a.duration;
            if(isFinite(d) && d > 0) {
              setDuration(d);
            } else {
              // webm Infinity duration — trigger duration detection without corrupting audio
              a.addEventListener("durationchange", function onDc(){
                if(isFinite(a.duration) && a.duration > 0){
                  setDuration(a.duration);
                  a.currentTime = 0;
                  a.removeEventListener("durationchange", onDc);
                }
              });
              a.currentTime = 24*60*60; // seek to 24h, browser clamps to real end
            }
          }}
          style={{display:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {/* -10s */}
          <button onClick={()=>skip(-10)} title="Back 10s"
            style={{width:28,height:28,borderRadius:"50%",border:"1px solid #E5E7EB",
              background:"#F7F9FC",cursor:"pointer",color:C.muted,fontSize:10,fontWeight:700,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            -10
          </button>
          {/* play/pause */}
          <button onClick={togglePlay}
            style={{width:36,height:36,borderRadius:"50%",border:"none",
              background:primary,color:"#fff",cursor:"pointer",flexShrink:0,
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:`0 2px 8px ${primary}55`}}>
            {playing
              ?<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16"/>
                  <rect x="14" y="4" width="4" height="16"/>
                </svg>
              :<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
            }
          </button>
          {/* +10s */}
          <button onClick={()=>skip(10)} title="Forward 10s"
            style={{width:28,height:28,borderRadius:"50%",border:"1px solid #E5E7EB",
              background:"#F7F9FC",cursor:"pointer",color:C.muted,fontSize:10,fontWeight:700,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            +10
          </button>
          <span style={{fontSize:10,color:C.hint,fontFamily:"monospace",
            flexShrink:0,minWidth:76}}>
            {fmtTime(current)} / {fmtTime(duration)}
          </span>
          {/* progress bar */}
          <div onClick={seekBar}
            style={{flex:1,height:5,background:"#E5E7EB",borderRadius:3,
              cursor:"pointer",position:"relative",userSelect:"none"}}>
            {screenshots.map(s=>{
              const t=toSeconds(s.SCREENTIME);
              const p=duration?(t/duration)*100:0;
              return(
                <div key={s.ATTACHMENTID} onClick={e=>{e.stopPropagation();seek(t);}}
                  title={fmtTime(t)}
                  style={{position:"absolute",top:-2,width:3,height:9,borderRadius:2,
                    background:"#F59E0B",left:`${p}%`,transform:"translateX(-50%)",
                    cursor:"pointer",zIndex:1}}/>
              );
            })}
            <div style={{position:"absolute",left:0,top:0,height:"100%",
              borderRadius:3,background:primary,width:`${pct}%`,
              transition:"width .05s",pointerEvents:"none"}}/>
          </div>
        </div>
      </div>

      {/* body: flippable layout */}
      <div style={{flex:1,overflow:"hidden",display:"flex",
        flexDirection:imageView?"row-reverse":"row"}}>

        {/* transcript */}
        {hasTranscript&&(
          <div ref={transRef}
            style={{flex:1,overflowY:"auto",padding:"10px 12px",
              borderLeft:imageView&&screenshots.length>0?`1px solid ${C.border}`:"none",
              borderRight:!imageView&&screenshots.length>0?`1px solid ${C.border}`:"none"}}>
            <div style={{fontSize:10,fontWeight:700,color:C.hint,
              textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6,
              display:"flex",alignItems:"center",gap:6}}>
              <span>Transcript</span>
              {serverSaved&&(
                <span style={{fontSize:10,color:"#16A34A",fontWeight:600,
                  textTransform:"none",letterSpacing:0}}>✓ Changes saved</span>
              )}
            </div>

            {/* pending edits bar */}
            {isAdmin&&pendingEdits>0&&(
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"8px 12px",borderRadius:8,marginBottom:10,
                background:"#FFFBEB",border:"1px solid #FCD34D"}}>
                <span style={{fontSize:12,color:"#92400E",fontWeight:500}}>
                  {pendingEdits} change{pendingEdits!==1?"s":""} pending
                </span>
                <button onClick={saveToServer} disabled={serverSaving}
                  style={{padding:"4px 14px",borderRadius:6,border:"none",
                    background:serverSaving?"#aaa":"#D97706",color:"#fff",
                    fontSize:12,fontWeight:700,cursor:serverSaving?"wait":"pointer",
                    flexShrink:0}}>
                  {serverSaving?"Saving…":"Publish changes"}
                </button>
              </div>
            )}
            {segments.map((seg,i)=>(
              <div key={i} data-seg={i}>
                <Segment
                  seg={seg} idx={i}
                  isActive={i===activeIdx}
                  isAdmin={isAdmin}
                  onSeek={seek}
                  primary={primary}
                  onLocalSave={(idx,newHtml)=>{
                    setSegments(prev=>prev.map((s,si)=>si===idx?{...s,html:newHtml}:s));
                    setPendingEdits(n=>n+1);
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* screenshots */}
        {screenshots.length>0&&(
          <div style={{
              width: imageView ? (hasTranscript?"55%":"100%") : (hasTranscript?180:undefined),
              flex: !hasTranscript?1:undefined,
              flexShrink:0,overflowY:"auto",
              padding: imageView?"10px 12px":"10px 10px",
              background:"#F8FAFC",transition:"width .2s"}}>
            <div style={{fontSize:10,fontWeight:700,color:C.hint,
              textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6,
              display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span>Slides</span>
              {hasTranscript&&(
                <button onClick={()=>setImageView(v=>!v)}
                  title={imageView?"Show transcript wide":"Expand slides"}
                  style={{background:"none",border:"1px solid #E5E7EB",cursor:"pointer",
                    borderRadius:5,padding:"2px 7px",fontSize:10,color:C.muted,
                    transition:"all .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=primary}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="#E5E7EB"}>
                  {imageView?"← Text":"⤢ Expand"}
                </button>
              )}
            </div>
            <ScreenshotPanel screenshots={screenshots} currentTime={current}
              onSeek={(t)=>{ seek(t); if(!imageView) setImageView(true); }}
              primary={primary} imageView={imageView}/>
          </div>
        )}

      </div>
    </div>
  );
}